import React from 'react'
import { cn } from '@amusnet/react-ui'
import { useTranslation } from '@amusnet/translations/react'
import { useUltraPlayStore, useConfigurationStore } from '@stores'
import { ViewportType } from 'game-ui-types'
import amusnetLogoSvg from '@assets/ultra-play/ultra-play-amusnet-logo.svg'
import { UltraPlayModalButton } from '@components'
import { playUltraPlaySound, ULTRA_PLAY_SOUND } from './ultraPlaySound'
import { ultraPlayAnalytics } from './ultraPlayAnalytics'

interface UltraPlayFeatureDialogProps {
    // Full Ultra Play close/reset (defined in UltraPlayModal) — restores speed,
    // rejects buy bonus, resets store and closes the modal.
    onClose: () => void
}

const UltraPlayFeatureDialog: React.FC<UltraPlayFeatureDialogProps> = ({ onClose }) => {
    const { t } = useTranslation()
    const setShowFeatureChoiceDialog = useUltraPlayStore((s) => s.setShowFeatureChoiceDialog)
    const isAutoplayable = useUltraPlayStore((s) => s.featureDialogAutoplayable)
    const resume = useUltraPlayStore((s) => s.resume)
    const viewport = useConfigurationStore((s) => s.viewport)

    const isPortrait = viewport === ViewportType.MOBILE_PORTRAIT
    // Landscape/desktop: span only the left (table) column — same width as it.
    const widthClass = isPortrait
        ? 'w-full'
        : viewport === ViewportType.DESKTOP
          ? 'w-[calc(100%-620px)]'
          : 'w-[calc(100%-450px)]'

    // Slide the sheet up from the bottom of the screen on mount.
    const [isVisible, setIsVisible] = React.useState(false)

    React.useEffect(() => {
        const raf = requestAnimationFrame(() => setIsVisible(true))

        return () => cancelAnimationFrame(raf)
    }, [])

    // Close Ultra Play entirely so the player can play the feature in the game.
    const handlePlayInGame = () => {
        ultraPlayAnalytics.featureDialogChoice('play_in_game')
        setShowFeatureChoiceDialog(false)
        onClose()
    }

    // Keep playing the feature inside Ultra Play with autoplay.
    const handleContinueHere = () => {
        ultraPlayAnalytics.featureDialogChoice('continue_here')
        playUltraPlaySound(ULTRA_PLAY_SOUND.PLAY)
        setShowFeatureChoiceDialog(false)
        ultraPlayAnalytics.sessionResumed('feature')
        resume()
    }

    return (
        <>
            {/* Click blocker — prevents interaction with the controls behind the sheet */}
            <div className="absolute inset-0 z-10" />

            <div
                className={cn(
                    'absolute bottom-0 left-0 z-10 h-[500px] transition-transform duration-300 ease-out',
                    widthClass,
                    isVisible ? 'translate-y-0' : 'translate-y-full'
                )}
            >
                {/* Gradient border (top + sides only; bottom is flush with the screen edge) */}
                <div className="w-full h-full rounded-t-[50px] px-0.5 pt-0.5 bg-ultra-feature-border shadow-[0px_6px_4px_0px_#00000080]">
                    <div className="relative w-full h-full overflow-hidden flex flex-col items-center justify-center rounded-t-[48px] bg-ultra-surface shadow-[0px_0px_40px_0px_#3CFF0066_inset]">
                        {/* Decorative glow logo — above the background, behind the text */}
                        <img
                            src={amusnetLogoSvg}
                            alt=""
                            className="absolute left-1/2 top-[-40px] -translate-x-1/2 w-[1908px] max-w-fit pointer-events-none select-none"
                        />

                        <div className="relative flex flex-col items-center">
                            <h2 className="text-center text-white font-black text-[40px] leading-none mb-[47px] [text-shadow:0px_4px_0px_#000000]">
                                {t('common.specialFeature')}
                            </h2>

                            <div className="flex flex-col gap-[20px]">
                                {isAutoplayable && (
                                    <UltraPlayModalButton
                                        id="ultra-play-continue-here"
                                        text={t('common.continueHere')}
                                        isPrimary
                                        onClick={handleContinueHere}
                                    />
                                )}
                                <UltraPlayModalButton
                                    id="ultra-play-feature-in-game"
                                    text={t('common.playFeatureInGame')}
                                    isPrimary={!isAutoplayable}
                                    onClick={handlePlayInGame}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default UltraPlayFeatureDialog
